import React, { useEffect } from 'react';
import { Meta } from 'react-head';
import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import LoadingIndicator from '@wix/da-shared-react/pkg/LoadingIndicator';
import { PapiDeviation } from '@wix/da-types-papi';
import OnScrollBottom, {
  DISTANCE_UNIT,
} from '@wix/da-shared-react/pkg/OnScrollBottom';
import FoldersHeader from '../FoldersHeader';
import FolderSlider from '../FolderSlider';
import GallectionDeviationsTorpedo from '../GallectionDeviationsTorpedo';
import SearchResultsBar from './SearchResultsBar';
import SearchEmptyState from './SearchEmptyState';
import {
  GallectionSortOrder,
  GallectionType,
} from '../../../../../types/gallection';

export type Props = {
  streamId: string;
  streamIsInitialized: boolean;
  streamItems: PapiDeviation[];
  streamHasMore: boolean;
  streamIsFetching: boolean;
  streamTotalCount: number;
  streamSearchQuery: string;
  streamSortOrder: GallectionSortOrder;
  streamInit: (
    searchQuery: string,
    sortOrder: GallectionSortOrder,
    gallectionType: GallectionType
  ) => void;
  streamFetchMore: (gallectionType: GallectionType) => void;
  searchQuery: string;
  gallectionType: GallectionType;
  sortOrder: GallectionSortOrder;
};

export type GallectionSearchViewProps = Props;

export const GallectionSearchView: React.FC<
  GallectionSearchViewProps
> = props => {
  // re-init stream if search query changed
  useEffect(() => {
    if (
      props.searchQuery !== props.streamSearchQuery ||
      props.sortOrder !== props.streamSortOrder
    ) {
      props.streamInit(
        props.searchQuery,
        props.sortOrder,
        props.gallectionType
      );
      props.streamFetchMore(props.gallectionType);
    }
  }, [
    props.searchQuery,
    props.streamSearchQuery,
    props.sortOrder,
    props.streamSortOrder,
  ]);

  const isLoadingFirstResults =
    !props.streamItems.length && props.streamHasMore;
  const isLoadingMore = props.streamItems.length > 0 && props.streamIsFetching;
  const isLoading = isLoadingFirstResults || isLoadingMore;

  const isEmpty =
    props.streamIsInitialized &&
    !props.streamItems.length &&
    !props.streamHasMore;

  const onScrollBottom = () => {
    props.streamHasMore &&
      !props.streamIsFetching &&
      props.streamFetchMore(props.gallectionType);
  };

  return (
    <ErrorBoundary>
      <Meta name="robots" content="noindex" />
      <FoldersHeader />
      <FolderSlider />
      <SearchResultsBar
        searchQuery={props.searchQuery}
        resultsTotal={props.streamTotalCount}
        isSearching={isLoadingFirstResults}
      />
      {props.streamItems.length > 0 && (
        <OnScrollBottom
          distanceToBottom={1}
          distanceUnit={DISTANCE_UNIT.WINDOW_HEIGHT}
          onScrolledToBottom={onScrollBottom}
        >
          <div /* needed for OnScrollBottom */>
            <GallectionDeviationsTorpedo
              streamId={props.streamId}
              gallectionType={props.gallectionType}
              deviations={props.streamItems}
            />
          </div>
        </OnScrollBottom>
      )}
      {isEmpty && <SearchEmptyState />}
      {(!props.streamIsInitialized || isLoading) && <LoadingIndicator />}
    </ErrorBoundary>
  );
};
GallectionSearchView.displayName = 'GallectionSearchView';

export default GallectionSearchView;
